/* eslint-disable react/prop-types */
import React, { Component } from "react";
import Button from "@material-ui/core/Button";
import CssBaseline from "@material-ui/core/CssBaseline";
import Link from "@material-ui/core/Link";
import Grid from "@material-ui/core/Grid";
import Box from "@material-ui/core/Box";
import Typography from "@material-ui/core/Typography";
import withStyles from "@material-ui/core/styles/withStyles";
import Container from "@material-ui/core/Container";
import Card from "@material-ui/core/Card";
import CardContent from "@material-ui/core/CardContent";
import FitnessCenterIcon from "@material-ui/icons/FitnessCenter";
import TimelineIcon from "@material-ui/icons/Timeline";
import PeopleIcon from "@material-ui/icons/People";
import EventIcon from "@material-ui/icons/Event";
import DirectionsRunIcon from "@material-ui/icons/DirectionsRun";
import WatchIcon from "@material-ui/icons/Watch";

import { MadeWithLove } from "components/MadeWithLove/MadeWithLove.jsx";
import Pricing from "components/Pricing/Pricing.jsx";
import { setAccessToken } from "containers/authenticated.js";
import { verifyLogin } from "services/authProvider.js";

import { staticData } from "config.js";

const styles = theme => ({
  root: {
    minHeight: "100vh",
    backgroundColor: "#f5f7fa"
  },
  heroSection: {
    backgroundColor: "#1976d2",
    color: "white",
    padding: "80px 0 60px",
    textAlign: "center",
    background: "linear-gradient(135deg, #667eea 0%, #764ba2 100%)"
  },
  heroTitle: {
    fontWeight: 700,
    marginBottom: "20px",
    fontSize: "3rem",
    [theme.breakpoints.down("sm")]: {
      fontSize: "2rem"
    }
  },
  heroSubtitle: {
    fontSize: "1.3rem",
    marginBottom: "40px",
    opacity: 0.9,
    maxWidth: "700px",
    margin: "0 auto 40px",
    [theme.breakpoints.down("sm")]: {
      fontSize: "1rem"
    }
  },
  ctaButton: {
    padding: "12px 48px",
    fontSize: "1.1rem",
    fontWeight: 600,
    marginRight: "16px",
    marginBottom: "16px",
    textTransform: "none",
    boxShadow: "0 4px 14px rgba(0,0,0,0.2)"
  },
  featuresSection: {
    padding: "80px 0",
    backgroundColor: "#ffffff"
  },
  sectionTitle: {
    textAlign: "center",
    marginBottom: "60px",
    fontSize: "2.5rem",
    fontWeight: 700,
    color: "#333",
    [theme.breakpoints.down("sm")]: {
      fontSize: "1.8rem",
      marginBottom: "40px"
    }
  },
  featureCard: {
    height: "100%",
    padding: "30px",
    transition: "transform 0.3s ease, box-shadow 0.3s ease",
    "&:hover": {
      transform: "translateY(-8px)",
      boxShadow: "0 12px 24px rgba(0,0,0,0.15)"
    }
  },
  featureIcon: {
    fontSize: "3.5rem",
    color: "#667eea",
    marginBottom: "20px"
  },
  featureTitle: {
    fontSize: "1.5rem",
    fontWeight: 600,
    marginBottom: "16px",
    color: "#333"
  },
  featureDescription: {
    fontSize: "1rem",
    color: "#666",
    lineHeight: 1.6
  },
  valueSection: {
    backgroundColor: "#f8f9fa",
    padding: "60px 0",
    textAlign: "center"
  },
  valueTitle: {
    fontSize: "2rem",
    fontWeight: 600,
    marginBottom: "20px",
    color: "#333"
  },
  valueText: {
    fontSize: "1.1rem",
    color: "#666",
    maxWidth: "800px",
    margin: "0 auto 40px",
    lineHeight: 1.7
  },
  footer: {
    backgroundColor: "#2c3e50",
    color: "white",
    padding: "40px 0",
    textAlign: "center"
  },
  footerLinks: {
    marginTop: "20px",
    "& a": {
      color: "white",
      margin: "0 16px",
      textDecoration: "none",
      "&:hover": {
        textDecoration: "underline"
      }
    }
  }
});

class StartPage extends Component {
  static getDerivedStateFromProps(props, state) {
    let searchPath = props.location && props.location.search;
    if (searchPath) {
      let tempList = searchPath.split("&");
      let token = tempList.length > 1 ? tempList[1] : null;
      token = token.replace("token=", "");
      // console.log("token", token)
      if (token) {
        setAccessToken({ token: token });
        verifyLogin().then(() => {
          props.history.push("/dashboard");
        });
      }
    }
    return state;
  }

  constructor(props) {
    super(props);
    this.state = {
      errorMessage: null
    };
  }

  handleGetStarted = () => {
    this.props.history.push("/signin");
  };

  handleSignIn = () => {
    this.props.history.push("/signin");
  };

  handleChange = name => event => {
    this.setState({ [name]: event.target.value });
  };

  render() {
    const { classes } = this.props;

    const features = [
      {
        icon: <FitnessCenterIcon className={classes.featureIcon} />,
        title: "Personalized Training Plans",
        description:
          "Get individualized training plans based on your VO2 Max, lactate threshold, and current fitness level. No more guesswork—train with precision tailored to your unique physiology."
      },
      {
        icon: <TimelineIcon className={classes.featureIcon} />,
        title: "Intelligent Adaptive System",
        description:
          "Your plan evolves with you. With seamless Strava and Garmin integration, our system monitors your performance and automatically adjusts your training to keep you progressing optimally."
      },
      {
        icon: <PeopleIcon className={classes.featureIcon} />,
        title: "Expert-Designed Methodology",
        description:
          "Training plans created by certified coaches and experienced runners. Benefit from evidence-based protocols backed by exercise science and proven results from elite programs."
      },
      {
        icon: <EventIcon className={classes.featureIcon} />,
        title: "Direct Coach Access",
        description:
          "Book one-on-one consultations with experienced coaches for personalized advice on race strategy, form analysis, injury prevention, and achieving your specific running goals."
      }
    ];

    return (
      <div className={classes.root}>
        <CssBaseline />

        {/* Hero Section */}
        <div className={classes.heroSection}>
          <Container maxWidth="lg">
            <Box mb={3}>
              <img
                alt="Runnboard Logo"
                src={staticData.company.logoURL}
                style={{ maxWidth: "200px", marginBottom: "30px" }}
              />
            </Box>
            <Typography variant="h1" className={classes.heroTitle}>
              Train Smarter, Run Better
            </Typography>
            <Typography className={classes.heroSubtitle}>
              Achieve your running goals with personalized, data-driven training
              plans that adapt to your progress. Combine advanced physiological
              metrics with expert coaching for optimal performance.
            </Typography>
            <Box>
              <Button
                variant="contained"
                color="secondary"
                size="large"
                className={classes.ctaButton}
                onClick={this.handleGetStarted}
              >
                Start Free Trial
              </Button>
              <Button
                variant="outlined"
                size="large"
                className={classes.ctaButton}
                onClick={this.handleSignIn}
                style={{
                  color: "white",
                  borderColor: "white"
                }}
              >
                Sign In
              </Button>
            </Box>
          </Container>
        </div>

        {/* Features Section */}
        <div className={classes.featuresSection}>
          <Container maxWidth="lg">
            <Typography variant="h2" className={classes.sectionTitle}>
              Why Choose Runnboard?
            </Typography>
            <Grid container spacing={4}>
              {features.map((feature, index) => (
                <Grid item xs={12} sm={6} md={6} key={index}>
                  <Card className={classes.featureCard} elevation={2}>
                    <CardContent>
                      <Box textAlign="center">{feature.icon}</Box>
                      <Typography
                        variant="h5"
                        className={classes.featureTitle}
                        align="center"
                      >
                        {feature.title}
                      </Typography>
                      <Typography
                        className={classes.featureDescription}
                        align="center"
                      >
                        {feature.description}
                      </Typography>
                    </CardContent>
                  </Card>
                </Grid>
              ))}
            </Grid>
          </Container>
        </div>

        {/* Value Proposition Section */}
        <div className={classes.valueSection}>
          <Container maxWidth="lg">
            <Typography variant="h3" className={classes.valueTitle}>
              Eliminate Guesswork from Your Training
            </Typography>
            <Typography className={classes.valueText}>
              Whether you&apos;re a recreational runner seeking structure, a
              competitive athlete preparing for your next PR, or returning from
              injury, Runnboard provides the expert guidance you need. Our
              platform combines cutting-edge algorithms with professional
              coaching wisdom to deliver training that works for you.
            </Typography>
            <Button
              variant="contained"
              color="primary"
              size="large"
              className={classes.ctaButton}
              onClick={this.handleGetStarted}
            >
              Get Started Now
            </Button>
          </Container>
        </div>

        {/* Integration Highlights */}
        <div className={classes.featuresSection}>
          <Container maxWidth="md">
            <Typography
              variant="h4"
              align="center"
              style={{ marginBottom: "30px", fontWeight: 600 }}
            >
              Seamlessly Integrates With Your Tools
            </Typography>
            <Typography
              align="center"
              style={{
                fontSize: "1.1rem",
                color: "#666",
                marginBottom: "40px"
              }}
            >
              Connect your Strava or Garmin account and let Runnboard
              automatically sync your activities, monitor your progress, and
              adapt your training plan in real-time.
            </Typography>
            <Grid container spacing={3} justify="center">
              <Grid item>
                <Box
                  textAlign="center"
                  style={{
                    padding: "20px",
                    transition: "transform 0.3s ease"
                  }}
                >
                  <DirectionsRunIcon
                    style={{
                      fontSize: "4rem",
                      color: "#FC4C02",
                      marginBottom: "10px"
                    }}
                  />
                  <Typography
                    style={{
                      fontWeight: 600,
                      fontSize: "1.2rem",
                      color: "#333"
                    }}
                  >
                    Strava
                  </Typography>
                </Box>
              </Grid>
              <Grid item>
                <Box
                  textAlign="center"
                  style={{
                    padding: "20px",
                    transition: "transform 0.3s ease"
                  }}
                >
                  <WatchIcon
                    style={{
                      fontSize: "4rem",
                      color: "#007CC3",
                      marginBottom: "10px"
                    }}
                  />
                  <Typography
                    style={{
                      fontWeight: 600,
                      fontSize: "1.2rem",
                      color: "#333"
                    }}
                  >
                    Garmin Connect
                  </Typography>
                </Box>
              </Grid>
            </Grid>
          </Container>
        </div>

        {/* Pricing Section */}
        <Pricing />

        {/* Footer */}
        <div className={classes.footer}>
          <Container maxWidth="lg">
            <Typography variant="h6" style={{ marginBottom: "10px" }}>
              Runnboard
            </Typography>
            <Typography variant="body2" style={{ opacity: 0.8 }}>
              Your personalized running training platform
            </Typography>
            <div className={classes.footerLinks}>
              <Link href="/signin">Sign In</Link>
              <Link href="/signin">Sign Up</Link>
              <Link href="/home">About</Link>
            </div>
            <Box mt={3}>
              <MadeWithLove />
            </Box>
          </Container>
        </div>
      </div>
    );
  }
}

export default withStyles(styles)(StartPage);
